import type { ArtifactThemeMode } from "@entrilia/shared/artifactRuntime/protocol";
import type { StateAction } from "@entrilia/shared/types";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import type {
  SendSmartViewArtifactMessageRequest,
  SmartViewArtifactAgentMode,
  SmartViewArtifactAgentResult,
  SmartViewArtifactVersionSummary,
} from "../../../../../api/types/smartViewArtifactsTypes";
import type {
  ArtifactCheckProblems,
  ArtifactCheckResult,
  ArtifactCheckStop,
  ArtifactCompareSkip,
  ArtifactPageProblem,
  ArtifactRuntimeError,
  ArtifactSourceNote,
} from "../canvas/checkResult";
import { CHECK_STEP_TIMEOUT_MS, needsFix } from "../canvas/checkResult";

export const STALE_ARTIFACT_MESSAGE = "The dashboard changed; reload it.";
export const TURN_FAILED_MESSAGE = "Sorry, an unexpected error occurred. Please try again.";

const MAX_AUTOMATIC_FIXES = 2;
const MAX_RUNTIME_ERROR_LENGTH = 16_384;
const MAX_FIX_ITEMS = 10;
const MAX_FIX_ITEM_LENGTH = 1_600;

export interface AgentChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  version?: number;
  savedAt?: string;
  notCopied?: string[];
}

export interface AgentFixRequest {
  version: number;
  runtimeError: string;
}

export interface AgentQuestion {
  question: string;
  options: string[];
}

export interface AgentClarification extends AgentQuestion {
  turnId: number;
  request: string;
  images: File[];
  fix?: AgentFixRequest;
}

export type AgentTurnResult =
  | { status: "running" }
  | { status: "saved"; version: number; summary?: string; notCopied?: string[] }
  | { status: "clarification"; clarification: AgentQuestion }
  | { status: "failed"; message: string; offerReload: boolean }
  | { status: "stopped" };

export type AgentFixTrigger = "automatic" | "requested";

export interface AgentCompareRequest {
  version: number;
  agentMode: SmartViewArtifactAgentMode;
}

export interface TurnModes {
  agentMode: SmartViewArtifactAgentMode;
  pictureMode: ArtifactThemeMode;
}

export interface PendingCompare extends TurnModes {
  images: File[];
}

export type CompareDecision = { kind: "start"; render: Blob } | { kind: "skip"; reason: string } | { kind: "none" };

export type RuntimeErrorFix = "automatic" | "offer" | "none";

export interface AgentTurnNote {
  severity: "warning" | "error";
  title: string;
  lines: string[];
}

export interface AgentTurn {
  id: number;
  prompt: string;
  images: File[];
  imageCount: number;
  answering?: AgentClarification;
  fix?: AgentFixRequest;
  compare?: AgentCompareRequest;
  compareSkipLine?: string;
  progress: string[];
  result: AgentTurnResult;
  notes: AgentTurnNote[];
  fixOffer?: AgentFixRequest;
  startedAt: number;
  endedAt?: number;
}

export interface AgentDraft {
  turnId: number;
  text: string;
}

export interface AgentChatState {
  turns: AgentTurn[];
  nextTurnId: number;
  pendingClarification?: AgentClarification;
  pendingCompare?: PendingCompare;
  draft?: AgentDraft;
  automaticFixCount: number;
  versionsWithFix: number[];
}

export const initialAgentChatState: AgentChatState = {
  turns: [],
  nextTurnId: 1,
  automaticFixCount: 0,
  versionsWithFix: [],
};

export const getRunningTurn = (state: AgentChatState): AgentTurn | undefined => {
  const turn = state.turns.at(-1);
  return turn?.result.status === "running" ? turn : undefined;
};

export const isTurnRunning = (state: AgentChatState): boolean => getRunningTurn(state) !== undefined;

export const getAnsweredTurnId = (state: AgentChatState): number | undefined => {
  const turn = state.turns.at(-1);
  if (!turn || turn.fix || turn.compare) {
    return undefined;
  }
  return turn.result.status === "saved" || turn.result.status === "clarification" ? turn.id : undefined;
};

const replaceLastTurn = (state: AgentChatState, turn: AgentTurn): AgentTurn[] => [...state.turns.slice(0, -1), turn];

const endTurn = (turn: AgentTurn, result: AgentTurnResult, endedAt: number): AgentTurn => ({
  ...turn,
  images: [],
  answering: undefined,
  result,
  endedAt,
});

const withoutFixOffers = (turns: AgentTurn[]): AgentTurn[] =>
  turns.map((turn) => (turn.fixOffer ? { ...turn, fixOffer: undefined } : turn));

const isSavingTurn =
  (version: number) =>
  (turn: AgentTurn): boolean =>
    turn.result.status === "saved" && turn.result.version === version;

const toSkipLine = (reason: string): string => `Compare skipped: ${reason}`;

const compareSkipReasons: Record<ArtifactCompareSkip, string> = {
  hidden: "the browser tab stayed in the background.",
  failed: "the picture of the dashboard could not be taken.",
};

const CHECK_UNFINISHED_REASON = "the dashboard check did not finish.";
const STILL_FAILING_REASON = "the dashboard still has errors.";
const STOPPED_REASON = "the agent was stopped.";

export const PAGE_RELOADED_LINE = toSkipLine("the page was reloaded.");

const dropPendingCompare = (state: AgentChatState, reason: string): AgentChatState => {
  const turn = state.turns.at(-1);
  if (!state.pendingCompare || !turn) {
    return state;
  }
  return {
    ...state,
    turns: replaceLastTurn(state, { ...turn, compareSkipLine: toSkipLine(reason) }),
    pendingCompare: undefined,
  };
};

const toHistoryMessages = ({
  version,
  createdAt,
  prompt,
  summary,
  notCopied,
}: SmartViewArtifactVersionSummary): AgentChatMessage[] => {
  const messages: AgentChatMessage[] = [];
  if (prompt) {
    messages.push({ id: `${version}-prompt`, role: "user", content: prompt });
  }
  if (summary || notCopied) {
    messages.push({
      id: `${version}-summary`,
      role: "assistant",
      content: summary ?? "",
      version,
      savedAt: createdAt,
      notCopied,
    });
  }
  return messages;
};

// Matches the prompt the backend stores for an answered clarification.
const getTurnRequest = (turn: AgentTurn): string =>
  turn.answering?.request ? `${turn.answering.request} (answer: ${turn.prompt})` : turn.prompt;

export const toChatHistory = (
  state: AgentChatState,
  versions: SmartViewArtifactVersionSummary[],
  latestVersion: number
): AgentChatMessage[] =>
  versions
    // The versions list can arrive before the artifact that names its newest version.
    .filter(({ version }) => version <= latestVersion && !state.turns.some(isSavingTurn(version)))
    .sort((left, right) => left.version - right.version)
    .flatMap(toHistoryMessages);

export const reloadHistoryAction: StateAction<AgentChatState> = (state) => ({
  ...state,
  turns: state.turns.filter((turn) => turn.id === state.pendingClarification?.turnId),
  pendingCompare: undefined,
});

export const startTurnAction =
  (prompt: string, attachedImages: File[] = [], startedAt = Date.now()): StateAction<AgentChatState> =>
  (state) => {
    if (isTurnRunning(state)) {
      return state;
    }
    const images = [...(state.pendingClarification?.images ?? []), ...attachedImages];
    const turn: AgentTurn = {
      id: state.nextTurnId,
      prompt,
      images,
      imageCount: images.length,
      answering: state.pendingClarification,
      progress: [],
      result: { status: "running" },
      notes: [],
      startedAt,
    };
    return {
      ...state,
      turns: [...withoutFixOffers(state.turns), turn],
      nextTurnId: state.nextTurnId + 1,
      pendingClarification: undefined,
      pendingCompare: undefined,
      automaticFixCount: 0,
    };
  };

const withoutRepeatedMessage = (stack: string | undefined, message: string): string | undefined => {
  const [firstLine, ...rest] = stack?.split("\n") ?? [];
  return firstLine === message ? rest.join("\n") : stack;
};

const uniqueByMessageAndView = <Item extends ArtifactPageProblem>(items: Item[]): Item[] =>
  distinctBy(items, ({ message, view }) => JSON.stringify([message, view]));

const describeFixPlace = (view: string, mode: ArtifactThemeMode): string => `View: ${view || "main"}, mode: ${mode}`;

const describeFixError = ({ message, stack, view, mode }: ArtifactRuntimeError): string =>
  [message, describeFixPlace(view, mode), withoutRepeatedMessage(stack, message)].filter(Boolean).join("\n");

const describeFixProblem = ({ message, view, mode }: ArtifactPageProblem): string =>
  `${message}\n${describeFixPlace(view, mode)}`;

export const toFixRequest = (
  version: number,
  { errors, problems, warnings }: ArtifactCheckProblems
): AgentFixRequest => {
  const items = [
    ...uniqueByMessageAndView(errors).map(describeFixError),
    ...uniqueByMessageAndView(problems).map(describeFixProblem),
  ];
  const parts = items
    .slice(0, MAX_FIX_ITEMS)
    .map((item, index) => `${index + 1}. ${item.slice(0, MAX_FIX_ITEM_LENGTH)}`);
  if (items.length > MAX_FIX_ITEMS) {
    parts.push(`And ${items.length - MAX_FIX_ITEMS} more problems.`);
  }
  if (warnings.length > 0) {
    parts.push(["Notes:", ...[...new Set(warnings)].map((warning) => `- ${warning}`)].join("\n"));
  }
  return { version, runtimeError: parts.join("\n\n").slice(0, MAX_RUNTIME_ERROR_LENGTH) };
};

export const toSendRequest = (
  turn: AgentTurn,
  latestVersion: number,
  mode: SmartViewArtifactAgentMode,
  builderView?: string
): SendSmartViewArtifactMessageRequest => {
  if (turn.compare) {
    const { version, agentMode } = turn.compare;
    return { messageUserInput: "", baseVersion: version, mode: agentMode, compare: true, images: turn.images };
  }
  if (turn.fix) {
    return { messageUserInput: "", baseVersion: turn.fix.version, mode, runtimeErrorUserInput: turn.fix.runtimeError };
  }
  const answeredFix = turn.answering?.fix;
  return {
    messageUserInput: turn.prompt,
    baseVersion: latestVersion,
    mode,
    runtimeErrorUserInput: answeredFix?.version === latestVersion ? answeredFix.runtimeError : undefined,
    pendingRequestUserInput: turn.answering?.request,
    pendingQuestionUserInput: turn.answering?.question,
    builderViewUserInput: builderView,
    images: turn.images,
  };
};

// Matches the backend's stored fix prompt; error text can carry data rows.
const FIX_PROMPT = "Automatic fix";

// Matches the backend's stored compare prompt.
const COMPARE_PROMPT = "Compare with the picture";

export const decideRuntimeErrorFix = (
  state: AgentChatState,
  version: number,
  latestVersion: number
): RuntimeErrorFix => {
  if (
    !state.turns.some(isSavingTurn(version)) ||
    state.versionsWithFix.includes(version) ||
    version !== latestVersion ||
    isTurnRunning(state)
  ) {
    return "none";
  }
  return state.automaticFixCount < MAX_AUTOMATIC_FIXES ? "automatic" : "offer";
};

export const startFixTurnAction =
  (fix: AgentFixRequest, trigger: AgentFixTrigger, startedAt = Date.now()): StateAction<AgentChatState> =>
  (state) => {
    if (isTurnRunning(state)) {
      return state;
    }
    const turn: AgentTurn = {
      id: state.nextTurnId,
      prompt: FIX_PROMPT,
      images: [],
      imageCount: 0,
      fix,
      progress: [],
      result: { status: "running" },
      notes: [],
      startedAt,
    };
    return {
      ...state,
      turns: [...withoutFixOffers(state.turns), turn],
      nextTurnId: state.nextTurnId + 1,
      automaticFixCount: trigger === "automatic" ? state.automaticFixCount + 1 : 0,
      versionsWithFix: [...new Set([...state.versionsWithFix, fix.version])],
    };
  };

export const addProgressAction =
  (message: string): StateAction<AgentChatState> =>
  (state) => {
    const turn = getRunningTurn(state);
    if (!turn) {
      return state;
    }
    return { ...state, turns: replaceLastTurn(state, { ...turn, progress: [...turn.progress, message] }) };
  };

const endTurnForRetry = (
  state: AgentChatState,
  turn: AgentTurn,
  result: AgentTurnResult,
  fixOffer: AgentFixRequest | undefined,
  endedAt: number
): AgentChatState => {
  const turns = replaceLastTurn(state, { ...endTurn(turn, result, endedAt), fixOffer });
  if (turn.fix || turn.compare) {
    return { ...state, turns };
  }
  return {
    ...state,
    turns,
    pendingClarification: turn.answering,
    draft: { turnId: turn.id, text: turn.prompt },
  };
};

export const failTurnAction =
  (message: string, endedAt = Date.now()): StateAction<AgentChatState> =>
  (state) => {
    const turn = getRunningTurn(state);
    if (!turn) {
      return state;
    }
    const offerReload = message === STALE_ARTIFACT_MESSAGE;
    const fixOffer = offerReload ? undefined : turn.fix;
    const ended = endTurnForRetry(state, turn, { status: "failed", message, offerReload }, fixOffer, endedAt);
    return turn.fix ? dropPendingCompare(ended, STILL_FAILING_REASON) : ended;
  };

export const stopTurnAction =
  (endedAt = Date.now()): StateAction<AgentChatState> =>
  (state) => {
    const turn = getRunningTurn(state);
    if (!turn) {
      return state;
    }
    const ended = endTurnForRetry(state, turn, { status: "stopped" }, turn.fix, endedAt);
    return turn.fix ? dropPendingCompare(ended, STOPPED_REASON) : ended;
  };

const toPendingCompare = (
  turn: AgentTurn,
  notCopied: string[] | undefined,
  modes: TurnModes | undefined
): PendingCompare | undefined => {
  if (!modes || notCopied === undefined || turn.compare || turn.images.length === 0) {
    return undefined;
  }
  return { images: turn.images, ...modes };
};

export const finishTurnAction =
  (result: SmartViewArtifactAgentResult, modes?: TurnModes, endedAt = Date.now()): StateAction<AgentChatState> =>
  (state) => {
    const turn = getRunningTurn(state);
    if (!turn) {
      return state;
    }
    if (result.success && result.version !== null) {
      const notCopied = result.notCopied ?? undefined;
      const saved: AgentTurnResult = {
        status: "saved",
        version: result.version,
        summary: result.summary ?? undefined,
        notCopied,
      };
      return {
        ...state,
        turns: replaceLastTurn(state, endTurn(turn, saved, endedAt)),
        pendingCompare: toPendingCompare(turn, notCopied, modes) ?? state.pendingCompare,
      };
    }
    if (result.clarification) {
      const question: AgentQuestion = {
        question: result.clarification.question,
        options: result.clarification.options,
      };
      const clarification: AgentClarification = {
        ...question,
        turnId: turn.id,
        request: turn.compare ? "" : getTurnRequest(turn),
        images: turn.compare ? [] : turn.images,
        fix: turn.fix ?? turn.answering?.fix,
      };
      return {
        ...state,
        turns: replaceLastTurn(state, endTurn(turn, { status: "clarification", clarification: question }, endedAt)),
        pendingClarification: clarification,
      };
    }
    return failTurnAction(result.errorMessage ?? TURN_FAILED_MESSAGE, endedAt)(state);
  };

const describeWhere = (view: string, viewTitle: string, mode: ArtifactThemeMode): string =>
  `${view === "" ? "Main view" : `"${viewTitle || view}" view`}, ${mode} mode`;

const describeFound = ({ view, viewTitle, mode, message }: ArtifactPageProblem): string =>
  `${describeWhere(view, viewTitle, mode)}: ${message}`;

const describeCheckStop = (stop: ArtifactCheckStop): string => {
  const minutes = CHECK_STEP_TIMEOUT_MS / 60_000;
  return stop.reason === "notStarted"
    ? `The dashboard did not start within ${minutes} minutes.`
    : `${describeWhere(stop.view, stop.viewTitle, stop.mode)}: did not finish loading within ${minutes} minutes.`;
};

const describeSourceNote = ({ sourceKey, sourceTitle, kind, message, view, viewTitle }: ArtifactSourceNote): string => {
  const source = `"${sourceTitle || sourceKey}" (${sourceKey})`;
  const where = view === "" ? "the main view" : `the "${viewTitle || view}" view`;
  return kind === "empty"
    ? `${source} returned no rows in ${where}.`
    : `${source} could not be loaded in ${where}: ${message}`;
};

const toProblemNotes = (version: number, { errors, problems }: ArtifactCheckProblems): AgentTurnNote[] => {
  const notes: AgentTurnNote[] = [];
  if (errors.length > 0) {
    notes.push({
      severity: "error",
      title: `Errors in version ${version}`,
      lines: distinctBy(errors, ({ message }) => message).map(describeFound),
    });
  }
  if (problems.length > 0) {
    notes.push({
      severity: "error",
      title: `Version ${version} shows problems on the page`,
      lines: uniqueByMessageAndView(problems).map(describeFound),
    });
  }
  return notes;
};

const toCheckNotes = (result: ArtifactCheckResult, hasFix: boolean): AgentTurnNote[] => {
  const { version, warnings, sourceNotes, stop } = result;
  const notes = hasFix ? [] : toProblemNotes(version, result);
  if (stop) {
    notes.push({
      severity: "warning",
      title: `Version ${version} could not be fully checked`,
      lines: [describeCheckStop(stop)],
    });
  }
  if (sourceNotes.length > 0) {
    notes.push({
      severity: "warning",
      title: `Data sources in version ${version}`,
      lines: [...new Set(sourceNotes.map(describeSourceNote))],
    });
  }
  if (warnings.length > 0) {
    notes.push({ severity: "warning", title: `Notes from checking version ${version}`, lines: [...new Set(warnings)] });
  }
  return notes;
};

export const addCheckResultAction =
  (result: ArtifactCheckResult): StateAction<AgentChatState> =>
  (state) => {
    const isSaving = isSavingTurn(result.version);
    const notes = toCheckNotes(result, state.versionsWithFix.includes(result.version));
    if (notes.length === 0 || !state.turns.some(isSaving)) {
      return state;
    }
    return {
      ...state,
      turns: state.turns.map((turn) => (isSaving(turn) ? { ...turn, notes: [...turn.notes, ...notes] } : turn)),
    };
  };

export const offerFixAction =
  (version: number, found: ArtifactCheckProblems): StateAction<AgentChatState> =>
  (state) => {
    const isSaving = isSavingTurn(version);
    if (!state.turns.some(isSaving)) {
      return state;
    }
    const notes = toProblemNotes(version, found);
    const fixOffer = toFixRequest(version, found);
    return dropPendingCompare(
      {
        ...state,
        turns: state.turns.map((turn) =>
          isSaving(turn) ? { ...turn, notes: [...turn.notes, ...notes], fixOffer } : turn
        ),
        versionsWithFix: [...new Set([...state.versionsWithFix, version])],
      },
      STILL_FAILING_REASON
    );
  };

export const getCompareMode = (
  state: AgentChatState,
  version: number,
  latestVersion: number
): ArtifactThemeMode | undefined =>
  version === latestVersion && !isTurnRunning(state) ? state.pendingCompare?.pictureMode : undefined;

export const decideCompare = (
  state: AgentChatState,
  result: ArtifactCheckResult,
  latestVersion: number
): CompareDecision => {
  if (!state.pendingCompare || isTurnRunning(state) || result.version !== latestVersion || needsFix(result)) {
    return { kind: "none" };
  }
  if (result.stop) {
    return { kind: "skip", reason: CHECK_UNFINISHED_REASON };
  }
  if (result.compareSkip) {
    return { kind: "skip", reason: compareSkipReasons[result.compareSkip] };
  }
  return result.compareRender
    ? { kind: "start", render: result.compareRender }
    : { kind: "skip", reason: compareSkipReasons.failed };
};

export const skipCompareAction =
  (reason: string): StateAction<AgentChatState> =>
  (state) =>
    dropPendingCompare(state, reason);

export const startCompareTurnAction =
  (render: File, version: number, startedAt = Date.now()): StateAction<AgentChatState> =>
  (state) => {
    const pending = state.pendingCompare;
    if (!pending || isTurnRunning(state)) {
      return state;
    }
    const images = [...pending.images, render];
    const turn: AgentTurn = {
      id: state.nextTurnId,
      prompt: COMPARE_PROMPT,
      images,
      imageCount: images.length,
      compare: { version, agentMode: pending.agentMode },
      progress: [],
      result: { status: "running" },
      notes: [],
      startedAt,
    };
    return { ...state, turns: [...state.turns, turn], nextTurnId: state.nextTurnId + 1, pendingCompare: undefined };
  };
